import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { ClassesService } from 'src/app/services/classes.service';
import { LanguageService } from 'src/app/services/language.service';
import {
  PickupBusItem,
  PickupItem,
  PickupPerson,
  PickupService,
} from 'src/app/services/pickup.service';
import { SearchableSelectOption } from 'src/app/shared/searchable-select/searchable-select.component';

@Component({
  selector: 'app-pickup-board',
  templateUrl: './pickup-board.component.html',
  styleUrls: ['./pickup-board.component.css'],
})
export class PickupBoardComponent implements OnInit, OnDestroy {
  classes: Array<{ id: string; name: string }> = [];
  selectedClassId = '';
  date = '';
  waiting: PickupItem[] = [];
  collected: PickupItem[] = [];
  buses: PickupBusItem[] = [];
  allBusesReady = false;
  allBusesReadyAt: number | null = null;
  loading = false;
  isError = false;
  errorMessage = '';
  showCollectConfirm = false;
  collecting = false;
  pendingCollectItem: PickupItem | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  isTeacher = false;

  constructor(
    private pickupService: PickupService,
    private classesService: ClassesService,
    private authService: AuthService,
    private languageService: LanguageService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.isTeacher = this.authService.isTeacher() && !this.authService.isAdmin();
    await this.loadClasses();
    await this.refresh();
    this.pollTimer = setInterval(() => {
      void this.refresh(true);
    }, 4000);
  }

  ngOnDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  get classOptions(): SearchableSelectOption[] {
    return this.classes.map(cls => ({
      value: cls.id,
      label: cls.name,
      searchText: cls.name,
    }));
  }

  async onClassChange(classId: string) {
    this.selectedClassId = classId || '';
    await this.refresh();
  }

  openScanner(): void {
    const base = this.router.url.includes('/teacher/')
      ? '/teacher'
      : this.router.url.includes('/supervisor/')
        ? '/supervisor'
        : '/main';
    this.router.navigate([base, 'pickup-scan'], {
      queryParams: this.selectedClassId ? { class: this.selectedClassId } : {},
    });
  }

  get pendingCollectName(): string {
    return this.displayName(this.pendingCollectItem?.student);
  }

  openCollectConfirm(item: PickupItem): void {
    if (this.collecting || !item?.student?.id) {
      return;
    }
    this.pendingCollectItem = item;
    this.showCollectConfirm = true;
  }

  async onCollectConfirm(confirmed: boolean): Promise<void> {
    if (!confirmed) {
      this.showCollectConfirm = false;
      this.pendingCollectItem = null;
      return;
    }

    const studentId = this.pendingCollectItem?.student?.id;
    if (!studentId || this.collecting) {
      return;
    }

    this.collecting = true;
    this.isError = false;
    try {
      await this.pickupService.collectWaitingStudent(studentId);
      this.showCollectConfirm = false;
      this.pendingCollectItem = null;
      await this.refresh(true);
    } catch (error: any) {
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'PICKUP.COLLECT_FAILED';
    } finally {
      this.collecting = false;
    }
  }

  displayName(person?: PickupPerson | null): string {
    if (!person) {
      return '—';
    }
    const isAr = this.languageService.current === 'ar';
    const primary = isAr
      ? `${person.firstNameAr || ''} ${person.lastNameAr || ''}`.trim()
      : `${person.firstNameEn || ''} ${person.lastNameEn || ''}`.trim();
    return primary
      || `${person.firstNameAr || ''} ${person.lastNameAr || ''}`.trim()
      || `${person.firstNameEn || ''} ${person.lastNameEn || ''}`.trim()
      || '—';
  }

  formatTime(ms: number | null | undefined): string {
    if (!ms) {
      return '';
    }
    try {
      return new Date(ms).toLocaleTimeString(this.languageService.current === 'ar' ? 'ar-AE' : 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }

  busLabel(item: PickupBusItem): string {
    const bus = item?.bus;
    if (!bus) {
      return '—';
    }
    const number = String(bus.number || '').trim();
    const name = String(bus.name || '').trim();
    if (number && name) {
      return `${number} - ${name}`;
    }
    return number || name || '—';
  }

  /** Newest call first (All Buses Ready + individual buses). */
  get orderedBusCards(): Array<
    | { kind: 'all-ready'; waitingAt: number }
    | { kind: 'bus'; waitingAt: number; item: PickupBusItem }
  > {
    const cards: Array<
      | { kind: 'all-ready'; waitingAt: number }
      | { kind: 'bus'; waitingAt: number; item: PickupBusItem }
    > = [];
    if (this.allBusesReady) {
      cards.push({ kind: 'all-ready', waitingAt: this.allBusesReadyAt || 0 });
    }
    for (const item of this.buses) {
      cards.push({ kind: 'bus', waitingAt: item.waitingAt || 0, item });
    }
    return cards.sort((a, b) => b.waitingAt - a.waitingAt);
  }

  private async loadClasses() {
    try {
      const classes = await this.classesService.list();
      this.classes = (classes || [])
        .map((cls: any) => ({ id: cls.id, name: cls.name }))
        .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name), 'ar'));
      if (!this.selectedClassId && this.classes.length) {
        this.selectedClassId = this.classes[0].id;
      }
    } catch {
      this.classes = [];
    }
  }

  async refresh(silent = false) {
    if (!silent) {
      this.loading = true;
    }
    this.isError = false;
    try {
      const board = await this.pickupService.getToday(this.selectedClassId || undefined);
      this.date = board.date;
      this.waiting = (board.waiting || [])
        .slice()
        .sort((a, b) => (b.waitingAt || 0) - (a.waitingAt || 0));
      this.collected = (board.collected || [])
        .slice()
        .sort((a, b) => (b.collectedAt || b.waitingAt || 0) - (a.collectedAt || a.waitingAt || 0));
      this.buses = board.buses || [];
      this.allBusesReady = !!board.allBusesReady;
      this.allBusesReadyAt = board.allBusesReadyAt || null;
    } catch (error: any) {
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'PICKUP.LOAD_FAILED';
    } finally {
      this.loading = false;
    }
  }
}
